import buildInfo from '../buildInfo.json'
import {
  getSWRegistration,
  notifyUpdateAvailable,
  notifyUpdateReady,
  scheduleStuckTimeout
} from '../hooks/useAppUpdate'

/**
 * Runtime safety net: polls /buildInfo.json every POLL_INTERVAL_MS. If the server's
 * buildId differs from the one baked into this bundle, we kick the service worker to
 * precache the new bundle and let the normal SW lifecycle wiring trigger the reload
 * once precaching is done. A stuck-SW safety net forces a reload after STUCK_SW_TIMEOUT_MS.
 */

const POLL_INTERVAL_MS = 2 * 60 * 1000 // 2 minutes
const STUCK_SW_TIMEOUT_MS = 60 * 1000
const BAKED_BUILD_ID: string = buildInfo.buildId

interface ServerBuildInfo {
  buildId?: string
}

async function fetchServerBuildId(): Promise<string | null> {
  try {
    const response = await fetch(`/buildInfo.json?t=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'omit'
    })
    if (!response.ok) {
      return null
    }
    const data = (await response.json()) as ServerBuildInfo
    return data.buildId ?? null
  } catch {
    return null
  }
}

export function startVersionChecker(): void {
  if (typeof window === 'undefined') {
    return
  }

  const check = async (): Promise<void> => {
    if (document.hidden) {
      return
    }
    const serverBuildId = await fetchServerBuildId()
    if (!serverBuildId || serverBuildId === BAKED_BUILD_ID) {
      return
    }

    const registration = getSWRegistration()
    if (!registration) {
      // No SW at all — can't precache, just show the brief modal and reload.
      notifyUpdateReady(true)
      return
    }

    // Show the modal with the full 45s bar, kick the SW to precache, and let the
    // SW lifecycle wiring in main.tsx trigger the reload once activation completes.
    // The stuck-SW safety net force-reloads if activation never fires.
    notifyUpdateAvailable()
    registration.update().catch(() => {
      // Swallow — if the update fetch fails, the stuck-SW timeout will catch it.
    })
    scheduleStuckTimeout(() => notifyUpdateReady(true), STUCK_SW_TIMEOUT_MS)
  }

  setInterval(check, POLL_INTERVAL_MS)

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      void check()
    }
  })
}
